/**
 * Advanced performance optimization utilities
 */

import { resourceHints } from './performance'

// Type definitions for gtag
declare global {
    function gtag(...args: any[]): void
}

// Type definitions for performance entries
interface PerformanceEntryWithProcessing extends PerformanceEntry {
    processingStart: number
}

/**
 * Critical resources that should be preloaded
 */
const CRITICAL_RESOURCES = [
    { href: '/_next/static/css/app.css', as: 'style' },
    { href: '/_next/static/chunks/main.js', as: 'script' },
    { href: '/_next/static/chunks/webpack.js', as: 'script' },
]

/**
 * Fonts that should be preloaded
 */
const CRITICAL_FONTS = [
    '/fonts/inter-var.woff2',
    '/fonts/jetbrains-mono.woff2',
]

/**
 * External domains to preconnect to
 */
const PRECONNECT_DOMAINS = [
    'https://cdn.sanity.io',
    'https://fonts.googleapis.com',
    'https://fonts.gstatic.com',
]

/**
 * Initialize performance optimizations
 */
export function initializePerformanceOptimizations(): void {
    if (typeof window === 'undefined') return

    // Preconnect to external domains
    PRECONNECT_DOMAINS.forEach(domain => {
        resourceHints.preconnect(domain, true)
    })

    // Preload critical fonts
    CRITICAL_FONTS.forEach(font => {
        resourceHints.preload(font, 'font', 'font/woff2')
    })

    // Preload critical resources
    CRITICAL_RESOURCES.forEach(({ href, as }) => {
        resourceHints.preload(href, as)
    })

    // Initialize intersection observer for lazy loading
    initializeLazyLoading()

    // Setup performance monitoring
    setupPerformanceMonitoring()

    // Initialize resource prioritization
    initializeResourcePrioritization()
}

/**
 * Initialize lazy loading with intersection observer
 */
function initializeLazyLoading(): void {
    if (!('IntersectionObserver' in window)) return

    const lazyImages = document.querySelectorAll('img[data-src]')
    const lazyComponents = document.querySelectorAll('[data-lazy]')

    const imageObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                const img = entry.target as HTMLImageElement
                const src = img.dataset.src
                if (src) {
                    img.src = src
                    img.removeAttribute('data-src')
                    imageObserver.unobserve(img)
                }
            }
        })
    }, {
        rootMargin: '50px 0px',
        threshold: 0.01
    })

    lazyImages.forEach(img => imageObserver.observe(img))

    const componentObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                const element = entry.target as HTMLElement
                const lazyComponent = element.dataset.lazy
                if (lazyComponent) {
                    // Trigger lazy component loading
                    element.dispatchEvent(new CustomEvent('lazy-load'))
                    componentObserver.unobserve(element)
                }
            }
        })
    }, {
        rootMargin: '100px 0px',
        threshold: 0.01
    })

    lazyComponents.forEach(component => componentObserver.observe(component))
}

/**
 * Setup performance monitoring
 */
function setupPerformanceMonitoring(): void {
    // Monitor Core Web Vitals
    if ('PerformanceObserver' in window) {
        // Monitor Largest Contentful Paint
        const lcpObserver = new PerformanceObserver((list) => {
            const entries = list.getEntries()
            const lastEntry = entries[entries.length - 1]

            // Report LCP to analytics (if available)
            if (typeof gtag !== 'undefined') {
                gtag('event', 'LCP', {
                    event_category: 'Web Vitals',
                    value: Math.round(lastEntry.startTime),
                    non_interaction: true,
                })
            }
        })

        try {
            lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true })
        } catch (e) {
            // Fallback for browsers that don't support LCP
        }

        // Monitor First Input Delay
        const fidObserver = new PerformanceObserver((list) => {
            const entries = list.getEntries()
            entries.forEach(entry => {
                const perfEntry = entry as PerformanceEntryWithProcessing
                const fid = perfEntry.processingStart - perfEntry.startTime

                // Report FID to analytics (if available)
                if (typeof gtag !== 'undefined') {
                    gtag('event', 'FID', {
                        event_category: 'Web Vitals',
                        value: Math.round(fid),
                        non_interaction: true,
                    })
                }
            })
        })

        try {
            fidObserver.observe({ type: 'first-input', buffered: true })
        } catch (e) {
            // Fallback for browsers that don't support FID
        }

        // Monitor Cumulative Layout Shift
        let clsValue = 0
        const clsObserver = new PerformanceObserver((list) => {
            const entries = list.getEntries()
            entries.forEach(entry => {
                if (!(entry as any).hadRecentInput) {
                    clsValue += (entry as any).value
                }
            })
        })

        try {
            clsObserver.observe({ type: 'layout-shift', buffered: true })
        } catch (e) {
            // Fallback for browsers that don't support CLS
        }

        // Report CLS when page is hidden
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'hidden' && clsValue > 0) {
                if (typeof gtag !== 'undefined') {
                    gtag('event', 'CLS', {
                        event_category: 'Web Vitals',
                        value: Math.round(clsValue * 1000),
                        non_interaction: true,
                    })
                }
            }
        })
    }
}

/**
 * Initialize resource prioritization
 */
function initializeResourcePrioritization(): void {
    // Prioritize above-the-fold content
    const aboveFoldImages = document.querySelectorAll('img[data-priority="high"]')
    aboveFoldImages.forEach(img => {
        const imgElement = img as HTMLImageElement
        if (imgElement.loading !== 'eager') {
            imgElement.loading = 'eager'
        }
        if (!imgElement.fetchPriority) {
            imgElement.fetchPriority = 'high'
        }
    })

    // Defer non-critical scripts
    const nonCriticalScripts = document.querySelectorAll('script[data-defer]')
    nonCriticalScripts.forEach(script => {
        const scriptElement = script as HTMLScriptElement
        if (!scriptElement.defer && !scriptElement.async) {
            scriptElement.defer = true
        }
    })
}

/**
 * Optimize images for better performance
 */
export function optimizeImage(
    src: string,
    options: {
        width?: number
        height?: number
        quality?: number
        format?: 'webp' | 'avif' | 'auto'
        priority?: boolean
    } = {}
): {
    src: string
    srcSet?: string
    sizes?: string
    loading: 'lazy' | 'eager'
    fetchPriority?: 'high' | 'low' | 'auto'
} {
    const {
        width,
        height,
        quality = 75,
        format = 'auto',
        priority = false
    } = options

    // For Sanity images
    if (src.includes('cdn.sanity.io')) {
        let optimizedSrc = src
        const params = new URLSearchParams()

        if (width) params.set('w', width.toString())
        if (height) params.set('h', height.toString())
        params.set('q', quality.toString())
        params.set('fit', 'max')
        params.set('auto', format)

        optimizedSrc = `${src}?${params.toString()}`

        // Generate srcSet for responsive images
        const srcSet = width ? [
            `${src}?w=${Math.round(width * 0.5)}&q=${quality}&fit=max&auto=${format} ${Math.round(width * 0.5)}w`,
            `${src}?w=${width}&q=${quality}&fit=max&auto=${format} ${width}w`,
            `${src}?w=${Math.round(width * 1.5)}&q=${quality}&fit=max&auto=${format} ${Math.round(width * 1.5)}w`,
            `${src}?w=${Math.round(width * 2)}&q=${quality}&fit=max&auto=${format} ${Math.round(width * 2)}w`,
        ].join(', ') : undefined

        return {
            src: optimizedSrc,
            srcSet,
            sizes: width ? `(max-width: ${width}px) 100vw, ${width}px` : undefined,
            loading: priority ? 'eager' : 'lazy',
            fetchPriority: priority ? 'high' : 'auto'
        }
    }

    // For other images, return basic optimization
    return {
        src,
        loading: priority ? 'eager' : 'lazy',
        fetchPriority: priority ? 'high' : 'auto'
    }
}

/**
 * Prefetch route for better navigation performance
 */
export function prefetchRoute(href: string): void {
    if (typeof window === 'undefined') return

    // Use Next.js router prefetch if available
    if (window.__NEXT_DATA__) {
        const link = document.createElement('link')
        link.rel = 'prefetch'
        link.href = href
        document.head.appendChild(link)
    }
}

/**
 * Optimize font loading
 */
export function optimizeFontLoading(): void {
    if (typeof document === 'undefined') return

    // Add font-display: swap to existing font faces
    const style = document.createElement('style')
    style.textContent = `
    @font-face {
      font-family: 'Inter';
      font-style: normal;
      font-weight: 100 900;
      font-display: swap;
      src: url('/fonts/inter-var.woff2') format('woff2');
    }
    
    @font-face {
      font-family: 'JetBrains Mono';
      font-style: normal;
      font-weight: 400 700;
      font-display: swap;
      src: url('/fonts/jetbrains-mono.woff2') format('woff2');
    }
  `
    document.head.appendChild(style)
}

/**
 * Critical CSS inlining utility
 */
export function inlineCriticalCSS(css: string): void {
    if (typeof document === 'undefined') return

    const style = document.createElement('style')
    style.textContent = css
    style.setAttribute('data-critical', 'true')
    document.head.insertBefore(style, document.head.firstChild)
}

/**
 * Bundle size analyzer for development
 */
export function analyzeBundleSize(): void {
    if (process.env.NODE_ENV !== 'development') return

    const scripts = Array.from(document.querySelectorAll('script[src]'))
    const styles = Array.from(document.querySelectorAll('link[rel="stylesheet"]'))

    console.group('📦 Bundle Analysis')

    scripts.forEach(script => {
        const src = (script as HTMLScriptElement).src
        if (src.includes('_next/static')) {
            console.log(`Script: ${src.split('/').pop()}`)
        }
    })

    styles.forEach(style => {
        const href = (style as HTMLLinkElement).href
        if (href.includes('_next/static')) {
            console.log(`Style: ${href.split('/').pop()}`)
        }
    })

    console.groupEnd()
}

/**
 * Memory usage monitoring
 */
export function monitorMemoryUsage(): void {
    if (typeof window === 'undefined' || !('performance' in window)) return

    const memory = (performance as any).memory
    if (memory) {
        console.group('🧠 Memory Usage')
        console.log(`Used: ${(memory.usedJSHeapSize / 1048576).toFixed(2)} MB`)
        console.log(`Total: ${(memory.totalJSHeapSize / 1048576).toFixed(2)} MB`)
        console.log(`Limit: ${(memory.jsHeapSizeLimit / 1048576).toFixed(2)} MB`)
        console.groupEnd()
    }
}

// Global performance optimization initialization
if (typeof window !== 'undefined') {
    // Initialize on DOM content loaded
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initializePerformanceOptimizations)
    } else {
        initializePerformanceOptimizations()
    }
}